import React from "react";
import {
  AppBar, Container, Box, Button, IconButton, ClickAwayListener, Collapse
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import iconeYoutube from "../assets/icone-youtube.png";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";
import br from "../assets/flag-br.png";
import us from "../assets/flag-us.png";
import es from "../assets/flag-es.png";

export default function Navbar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = React.useState(false);

  const { t, i18n } = useTranslation();

  const changeLang = (lng) => {
    i18n.changeLanguage(lng);
    document.documentElement.setAttribute("lang", lng);
  };

  const links = React.useMemo(() => ([
    { label: t("navbar.features"), href: "#o-que-fazemos" },
    { label: t("navbar.who"), href: "#quem-somos" },
    { label: t("navbar.cases"), href: "#cases" },
    { label: t("navbar.contact"), href: "#contato" }
  ]), [t]);

  const handleToggle = () => setOpen(v => !v);
  const closeMenu = () => setOpen(false);

  const goTo = (hash) => (e) => {
    if (e) e.preventDefault();
    closeMenu();
    const el = document.querySelector(hash);
    if (!el) return;

    const top = el.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({ top, behavior: "smooth" });
  };

  const [langMenuOpen, setLangMenuOpen] = React.useState(false);

  const availableLangs = [
    { code: "pt", label: "PT", flag: br, alt: "Português" },
    { code: "en", label: "EN", flag: us, alt: "English" },
    { code: "es", label: "ES", flag: es, alt: "Español" },
  ];

  const currentLangCode = (i18n.resolvedLanguage || i18n.language || "pt").slice(0, 2);
  const currentLang = availableLangs.find(l => l.code === currentLangCode) || availableLangs[0];

  const handleLanguageChange = (lng) => {
    localStorage.setItem("lang", lng);
    changeLang(lng);
    setLangMenuOpen(false);
  };

  React.useEffect(() => {
    const saved = localStorage.getItem("lang");
    if (saved && saved !== currentLangCode) {
      changeLang(saved);
    } else {
      localStorage.setItem("lang", currentLangCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppBar
      position="absolute"
      elevation={0}
      sx={{
        top: 0, left: 0, right: 0,
        background: "transparent",
        boxShadow: "none",
        py: { xs: 1, md: 2 },
        zIndex: 10,
      }}
    >
      <Container sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        {isMobile ? (
          <ClickAwayListener onClickAway={closeMenu}>
            <Box sx={{ position: "relative" }}>
              <IconButton
                aria-label="abrir menu"
                onClick={handleToggle}
                sx={{ color: "#00FF4F", width: 40, height: 40 }}
              >
                <MenuIcon sx={{ fontSize: 30 }} />
              </IconButton>
              <Collapse in={open} timeout={200} unmountOnExit>
                <Box
                  sx={{
                    position: "absolute",
                    top: { xs: 48, md: 56 },
                    left: 0,
                    zIndex: 11,
                    display: "grid",
                    gridTemplateColumns: "repeat(2, max-content)",
                    columnGap: 4,
                    rowGap: 2,
                    justifyItems: "start",
                    alignItems: "start",
                    background: "transparent",
                    px: 1,
                  }}
                >
                  {links.map((item) => (
                    <Button
                      key={item.href}
                      component="a"
                      href={item.href}
                      onClick={goTo(item.href)}
                      disableRipple
                      sx={{
                        color: "#ECEDF8",
                        fontWeight: 700,
                        fontSize: 16,
                        lineHeight: 1.25,
                        textTransform: "none",
                        minWidth: 0,
                        px: 0,
                        py: 0,
                        justifyContent: "flex-start",
                        whiteSpace: "nowrap",
                        background: "transparent",
                        "&:hover": { background: "transparent", color: "#fff" },
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Box>
              </Collapse>
            </Box>
          </ClickAwayListener>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: { sm: 1, md: 2 } }}>
            {links.map((item) => (
              <Button
                key={item.href}
                component="a"
                href={item.href}
                onClick={goTo(item.href)}
                color="inherit"
                sx={{ fontWeight: 600, fontSize: { sm: 14, md: 16 }, color: "#fff" }}
              >
                {item.label}
              </Button>
            ))}
          </Box>
        )}

        {/* ====== DIREITA: Idiomas + LinkedIn ====== */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, position: "relative" }}>
          <ClickAwayListener onClickAway={() => setLangMenuOpen(false)}>
            <Box sx={{ position: "relative" }}>
              <Button
                onClick={() => setLangMenuOpen(o => !o)}
                sx={{ color: "#fff", minWidth: 0, px: 1 }}
              >
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}>
                  {currentLang.label} <img src={currentLang.flag} alt={currentLang.alt} style={{ width: 16, height: 16 }} />
                </Box>
              </Button>
              {langMenuOpen && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "100%",
                    mt: 0.5,
                    right: 0,
                    left: "-10%",
                    backdropFilter: "blur(4px)",
                    borderRadius: 1,
                    p: 0.5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    zIndex: 20,
                    minWidth: 90,
                  }}
                >
                  {availableLangs.filter(l => l.code !== currentLang.code).map(l => (
                    <Button
                      key={l.code}
                      onClick={() => handleLanguageChange(l.code)}
                      sx={{
                        color: "#fff",
                        justifyContent: "flex-start",
                        px: 1,
                        minWidth: 0,
                        fontSize: 14,
                        gap: 0.5,
                      }}
                    >
                      {l.label} <img src={l.flag} alt={l.alt} style={{ width: 16, height: 16 }} />
                    </Button>
                  ))}
                </Box>
              )}
            </Box>
          </ClickAwayListener>

          <IconButton
            component="a"
            href="https://www.youtube.com/@benextai"
            target="_blank"
            rel="noopener"
            aria-label="YouTube Benext"
          >
            <Box component="img" src={iconeYoutube} alt="YouTube" sx={{ width: 32, height: 32 }} />
          </IconButton>

          <IconButton
            component="a"
            href="https://www.linkedin.com/company/benext-ai/"
            target="_blank"
            rel="noopener"
            aria-label="LinkedIn Benext"
            sx={{ color: "#00FF4F" }}
          >
            <LinkedInIcon sx={{ fontSize: 32 }} />
          </IconButton>
        </Box>
      </Container>
    </AppBar>
  );
}